import { Box, Button, Grid, Typography } from "@mui/material"
import React, { useCallback, useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import { customPalette } from "../../../../../../../../theme"
import { AddressDisplayer } from "../../../../../../../components/AddressDisplayer"
import { DeleteButton } from "../../../../../../../components/buttons/DeleteButton/DeleteButton"
import {
  BOTTOM_NAVIGATION_HEIGHT,
  CustomBottomNavigation,
} from "../../../../../../../components/buttons/navigate-button/CustomBottomNavigation"
import { InfoItem } from "../../../../../../../components/card/components/InfoItem"
import { IconEye } from "../../../../../../../components/custom-icons/icon-eye"
import { ImageDisplayer } from "../../../../../../../components/image-displayer/image-displayer"
import { SuccessContext } from "../../../../../../../components/layout/success-snackbar"
import { pagesUrl } from "../../../../../../../core/appConstants"
import { BSVariantContext } from "../../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { CoverImgProjectContext } from "../../../../../../../core/context/project/CoverImgProjectContext"
import { ProjectContext } from "../../../../../../../core/context/project/ProjectContext"
import { BSVariant } from "../../../../../../../core/dto/beem-shot/BSVariant/BSVariant"
import { BSVariantPatchDto } from "../../../../../../../core/dto/beem-shot/BSVariant/BSVariantNameAndDescriptionPatchDto"
import { useForm } from "../../../../../../../core/hooks/form/use-form"
import { required } from "../../../../../../../core/hooks/form/validation"
import { formatToFrench } from "../../../../../../../core/services/date-service"
import { resolveUrl } from "../../../../../../../core/services/http-service"
import { SeisimicStatusCard } from "../../components/BSVariant/SeisimicStatusCard"
import { BSVariantControlPanelContext } from "../../components/BSVariantControlPanelContext"
import { BSVariantFormCard } from "../../components/BSVariantFormCard"
import { DeleteVariantDialog } from "./DeleteVariantDialog"

interface IForm {
  name: string
  description: string
  surfaceParcelle: number
}

function formToDtoUpdate(form: IForm, variantId: string): BSVariantPatchDto {
  return {
    id: variantId,
    name: form.name,
    description: form.description,
    surfaceParcelle: form.surfaceParcelle,
  }
}

function dtoToForm(dto: BSVariant | undefined): IForm {
  return dto
    ? {
        name: dto.name,
        description: dto.description,
        surfaceParcelle: dto.surfaceParcelle,
      }
    : {
        name: "",
        description: "",
        surfaceParcelle: 0,
      }
}
interface IProps {
  bsVariant?: BSVariant
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>
  isUpdating: boolean
}

export function BSVariantInfosTab({ bsVariant, setIsUpdating, isUpdating }: Readonly<IProps>): React.JSX.Element {
  const navigate = useNavigate()

  const openSuccessSnackbar = useContext(SuccessContext)
  const { project } = useContext(ProjectContext)
  const { updateBSVariantNameAndDescription } = useContext(BSVariantContext)
  const { controlPanelRefWidth } = useContext(BSVariantControlPanelContext)
  const { file } = useContext(CoverImgProjectContext)

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

  const submit: (form: IForm) => Promise<any> = useCallback(
    (form: IForm) => {
      if (bsVariant?.id) {
        return updateBSVariantNameAndDescription(formToDtoUpdate(form, bsVariant.id)).then(() => {
          openSuccessSnackbar("Les informations de l'estimation ont été mises à jour.")
          setIsUpdating(false)
        })
      }
      return Promise.resolve()
    },
    [bsVariant?.id, openSuccessSnackbar, setIsUpdating, updateBSVariantNameAndDescription]
  )

  const { form, errors, handleChange, handleSubmit, isSubmitting } = useForm(
    bsVariant,
    dtoToForm,
    [required("name")],
    submit,
    () => setIsUpdating(true)
  )

  return !bsVariant ? (
    <Typography>Aucune estimation sélectionnée</Typography>
  ) : (
    <Box
      component="form"
      id="bs-variant-name-description-update"
      height={isUpdating ? `calc(100% - ${BOTTOM_NAVIGATION_HEIGHT} -  2vh)` : "100%"}
      onSubmit={handleSubmit}
      px={5}
      pb={1}
      sx={{ overflow: "auto" }}>
      <Typography variant="h5" fontWeight={600} sx={{ mt: 2 }}>
        Informations
      </Typography>

      <Typography fontSize={10} fontWeight={400} sx={{ color: customPalette.lightGray4, mb: 2 }}>
        {` Mise à jour le ${formatToFrench(project?.lastModifiedDate)}`}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", my: 2 }}>
        <DeleteButton label="Supprimer l'estimation" onClick={() => setOpenDeleteDialog(true)} />
      </Box>
      <BSVariantFormCard
        fieldName="name"
        label="Nom"
        errors={errors}
        form={form}
        handleChange={(e) => {
          setIsUpdating(true)
          handleChange(e)
        }}
        content={
          <Grid container rowGap={2} sx={{ background: customPalette.paperBackground, p: 2, borderRadius: 3 }}>
            <Grid container item xs={10} display="flex" alignItems="center" gap={1}>
              <Grid item xs={12} display="flex" alignItems="center">
                <Box p={1}>
                  <ImageDisplayer file={file} height={50} width={50} isLoading={false} />
                </Box>
                <Box>
                  <Typography variant="subtitle1"> {project?.name}</Typography>
                  <AddressDisplayer address={project?.address} />
                </Box>
              </Grid>

              <Grid item xs={12} container spacing={1} pt={1}>
                <Grid item xs={4}>
                  <InfoItem
                    label="Date de dépôt de permis"
                    value={formatToFrench(project?.licenceDate) ?? ""}
                    backgroundColor={customPalette.white}
                    hideBorder
                  />
                </Grid>
                {/* Uncomment when we have phase on the project */}
                {/*<Grid item xs={4}>*/}
                {/*  <InfoItem label="Phase actuelle" value="" />*/}
                {/*</Grid>*/}
                <Grid item xs={4}>
                  <InfoItem label="N° d'affaire" value={project?.businessCode ?? ""} />
                </Grid>
              </Grid>

              {false && (
                <Grid item xs={12} container pt={1}>
                  <SeisimicStatusCard />
                </Grid>
              )}
            </Grid>

            <Grid item xs={2} sx={{ display: "flex", height: "fit-content", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                color="info"
                onClick={() => navigate(resolveUrl(pagesUrl.PROJECT_PAGE_DETAIL, [project?.id]))}
                startIcon={<IconEye />}>
                <Typography>VOIR</Typography>
              </Button>
            </Grid>
          </Grid>
        }
      />

      <BSVariantFormCard
        title="Commentaires"
        fieldName="description"
        label="Description"
        errors={errors}
        form={form}
        required={false}
        handleChange={(e) => {
          setIsUpdating(true)
          handleChange(e)
        }}
        subTitle="Décrivez votre estimation de calcul ici pour conserver un historique de vos modifications."
      />

      <DeleteVariantDialog open={openDeleteDialog} setOpen={setOpenDeleteDialog} bsVariant={bsVariant} />

      {isUpdating && (
        <CustomBottomNavigation
          actionLabel="Enregistrer"
          formId={isUpdating ? "bs-variant-name-description-update" : ""}
          isSubmitting={isSubmitting}
          additionalLeftPadding={controlPanelRefWidth ?? 0}
        />
      )}
    </Box>
  )
}
